import { Position, useReactFlow } from '@xyflow/react';
import { useCallback, useEffect, useRef } from 'react';
import { DuplicatorToolbarButton } from './DuplicatorToolbarButton';
import { CanvasElementType } from '../../canvas-types';
import { uniqueId } from '../../../../lib/uniqueId';
import type { RfNodeStickyNote } from '../../canvas-types/nodeTypesData';

const OFFSET = 44;
const STICKY_NOTE_DEFAULT_COLOR = '#EBDEFF';

const calculateOffset = (currentNode: RfNodeStickyNote, position: Position) => {
  const nodeWidth = currentNode.width || 120;
  const nodeHeight = currentNode.height || 120;

  let offsetX = 0;
  let offsetY = 0;

  if (position === Position.Right) {
    offsetX = nodeWidth + OFFSET;
  } else if (position === Position.Left) {
    offsetX = -(nodeWidth + OFFSET);
  } else if (position === Position.Top) {
    offsetY = -(nodeHeight + OFFSET);
  } else if (position === Position.Bottom) {
    offsetY = nodeHeight + OFFSET;
  }

  return {
    x: currentNode.position.x + offsetX,
    y: currentNode.position.y + offsetY,
  };
};

export const StickyNodeDuplicators = ({ nodeId }: { nodeId: string }) => {
  const { getNode, setNodes } = useReactFlow();
  const temporaryNodeIdRef = useRef<string | null>(null);

  const onMouseEnter = useCallback(
    (position: Position) => {
      const currentNode = getNode(nodeId) as RfNodeStickyNote | undefined;
      if (!currentNode) return;

      // Remove previous temporary node if exists
      if (temporaryNodeIdRef.current) {
        const prevTempId = temporaryNodeIdRef.current;
        setNodes((nodes) => nodes.filter((n) => n.id !== prevTempId));
      }

      // Create temporary node
      const tempNodeId = `temp-${uniqueId()}`;
      temporaryNodeIdRef.current = tempNodeId;

      const temporaryNode: RfNodeStickyNote = {
        id: tempNodeId,
        type: CanvasElementType.STICKY_NOTE,
        position: calculateOffset(currentNode, position),
        data: {
          color: currentNode.data.color || STICKY_NOTE_DEFAULT_COLOR,
          richText: '',
          temporary: true,
        },
        width: currentNode.width,
        height: currentNode.height,
        selectable: false,
        draggable: false,
        parentId: currentNode.parentId,
      };

      setNodes((nodes) => [...nodes, temporaryNode]);
    },
    [nodeId, getNode, setNodes],
  );

  const onMouseLeave = useCallback(() => {
    if (temporaryNodeIdRef.current) {
      const tempId = temporaryNodeIdRef.current;
      setNodes((nodes) => nodes.filter((n) => n.id !== tempId));
      temporaryNodeIdRef.current = null;
    }
  }, [setNodes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (temporaryNodeIdRef.current) {
        const tempId = temporaryNodeIdRef.current;
        setNodes((nodes) => nodes.filter((n) => n.id !== tempId));
      }
    };
  }, [setNodes]);

  const onClick = useCallback(
    (position: Position) => {
      const currentNode = getNode(nodeId) as RfNodeStickyNote | undefined;
      if (!currentNode) return;

      // Remove temporary node if exists
      if (temporaryNodeIdRef.current) {
        const tempId = temporaryNodeIdRef.current;
        temporaryNodeIdRef.current = null;

        // Create permanent node
        const newNodeId = uniqueId();
        const permanentNode: RfNodeStickyNote = {
          id: newNodeId,
          type: CanvasElementType.STICKY_NOTE,
          position: calculateOffset(currentNode, position),
          data: {
            color: currentNode.data.color || STICKY_NOTE_DEFAULT_COLOR,
            richText: '',
            temporary: false,
            freshlyCreated: true,
          },
          width: currentNode.width,
          height: currentNode.height,
          selected: false,
          parentId: currentNode.parentId,
        };

        // Remove temporary and add permanent node
        setNodes((nodes) => [...nodes.filter((n) => n.id !== tempId), permanentNode]);
      }
    },
    [nodeId, getNode, setNodes],
  );

  return (
    <>
      <DuplicatorToolbarButton
        position={Position.Right}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
      <DuplicatorToolbarButton
        position={Position.Left}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
      <DuplicatorToolbarButton
        position={Position.Top}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
      <DuplicatorToolbarButton
        position={Position.Bottom}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
    </>
  );
};
